import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ShieldCheck, ShieldX, Eye, Target } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHero, Reveal, Spinner } from "@/components/site/Bits";
import { FaqList } from "@/pages/Home";
import { useSite, usePageTitle } from "@/lib/site";
import { publicApi, errMsg, fmtDate, fmtDateTime } from "@/lib/api";

export function About() {
  usePageTitle("من نحن");
  const { settings: s } = useSite();
  if (!s) return <Spinner />;
  return (
    <div data-testid="about-page">
      <PageHero eyebrow="تعرّف علينا" title="من نحن" subtitle={s.slogan} />
      <div className="container-x py-14 grid lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-7"><div className="card-lux !transform-none p-8"><p className="leading-loose text-lg whitespace-pre-line" data-testid="about-text">{s.about_text}</p></div></Reveal>
        <div className="lg:col-span-5 space-y-5">
          <Reveal delay={0.1}><div className="card-lux p-7"><Eye className="text-[var(--copper)] mb-3" /><h3 className="font-display font-bold text-lg">رؤيتنا</h3><p className="mt-2 text-[var(--muted-ink)] leading-relaxed">{s.vision}</p></div></Reveal>
          <Reveal delay={0.2}><div className="card-lux p-7"><Target className="text-[var(--copper)] mb-3" /><h3 className="font-display font-bold text-lg">رسالتنا</h3><p className="mt-2 text-[var(--muted-ink)] leading-relaxed">{s.mission}</p></div></Reveal>
        </div>
      </div>
    </div>
  );
}

export function Contact() {
  usePageTitle("تواصل معنا");
  const { settings: s } = useSite();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await publicApi.post("/contact", f); toast.success("تم إرسال رسالتك، سنتواصل معك قريباً"); setF({ name: "", email: "", phone: "", message: "" }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const c = s?.contact || {};
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="نسعد بتواصلك" title="تواصل معنا" subtitle="لأي استفسار عن الدورات أو التسجيل، راسلنا وسنرد عليك في أقرب وقت." />
      <div className="container-x py-14 grid lg:grid-cols-12 gap-8">
        <form onSubmit={submit} className="lg:col-span-7 card-lux !transform-none p-8 space-y-4" data-testid="contact-form">
          <Input required placeholder="الاسم" value={f.name} onChange={set("name")} className="h-12" data-testid="contact-name-input" />
          <Input required type="email" dir="ltr" placeholder="البريد الإلكتروني" value={f.email} onChange={set("email")} className="h-12" data-testid="contact-email-input" />
          <Input dir="ltr" placeholder="رقم الهاتف (اختياري)" value={f.phone} onChange={set("phone")} className="h-12" data-testid="contact-phone-input" />
          <Textarea required minLength={5} rows={6} placeholder="رسالتك" value={f.message} onChange={set("message")} data-testid="contact-message-input" />
          <button disabled={busy} className="btn-copper" data-testid="contact-submit-btn">{busy ? "جارٍ الإرسال..." : "إرسال الرسالة"}</button>
        </form>
        <div className="lg:col-span-5 space-y-4">
          {[[Phone, "الهاتف", c.phone], [Phone, "واتساب", c.whatsapp], [Mail, "البريد الإلكتروني", c.email], [MapPin, "العنوان", c.address]].map(([Icon, k, v]) => (
            <div key={k} className="card-lux p-6 flex items-center gap-4"><span className="w-11 h-11 rounded-xl bg-[#B87333]/10 grid place-items-center text-[var(--copper)]"><Icon size={20} /></span><div><p className="text-xs text-[var(--muted-ink)]">{k}</p><p className="font-semibold" dir="auto">{v}</p></div></div>
          ))}
        </div>
      </div>
    </div>
  );
}

const PAGES = { privacy: "سياسة الخصوصية", terms: "شروط الاستخدام", refund: "سياسة الإلغاء والاسترداد" };

export function InfoPage({ kind }) {
  usePageTitle(PAGES[kind]);
  const { settings: s } = useSite();
  return (
    <div data-testid={`${kind}-page`}>
      <PageHero title={PAGES[kind]} />
      <div className="container-x py-14 max-w-4xl"><div className="card-lux !transform-none p-8 md:p-10 leading-loose whitespace-pre-line text-[var(--ink)]" data-testid={`${kind}-content`}>{s?.[kind]}</div></div>
    </div>
  );
}

export function FAQ() {
  usePageTitle("الأسئلة الشائعة");
  const { settings: s } = useSite();
  return (
    <div data-testid="faq-page">
      <PageHero eyebrow="مركز المساعدة" title="الأسئلة الشائعة" />
      <div className="container-x py-14 max-w-4xl"><FaqList items={s?.faqs || []} /></div>
    </div>
  );
}

export function Verify() {
  usePageTitle("التحقق من الشهادات");
  const [sp] = useSearchParams();
  const [no, setNo] = useState(sp.get("no") || "");
  const [res, setRes] = useState(null);
  const run = async (value) => {
    if (!value.trim()) return;
    const r = await publicApi.get(`/certificates/verify/${encodeURIComponent(value.trim())}`);
    setRes(r.data);
  };
  const check = (e) => {
    e.preventDefault();
    run(no);
  };
  useEffect(() => {
    const q = sp.get("no");
    if (q) run(q);
  }, [sp]);
  return (
    <div data-testid="verify-page">
      <PageHero eyebrow="خدمة التحقق" title="التحقق من الشهادات" subtitle="أدخل رقم الشهادة أو امسح رمز QR الموجود عليها للتحقق من صحتها." />
      <div className="container-x py-14 max-w-2xl">
        <form onSubmit={check} className="flex gap-3" data-testid="verify-form">
          <Input dir="ltr" value={no} onChange={(e) => setNo(e.target.value)} placeholder="HTC-2026-XXXXXX" className="h-12 bg-white" data-testid="verify-input" />
          <button className="btn-copper shrink-0" data-testid="verify-submit-btn">تحقق</button>
        </form>
        {res && (res.valid ? (
          <div className="card-lux !transform-none p-8 mt-8 border-emerald-300" data-testid="verify-result-valid">
            <p className="flex items-center gap-2 text-emerald-700 font-bold text-lg"><ShieldCheck />شهادة صحيحة وموثقة</p>
            <div className="mt-5 space-y-2 text-sm">
              <p>اسم المتدرب: <b data-testid="verify-student-name">{res.student_name}</b></p>
              <p>الدورة: <b>{res.course_title}</b></p>
              <p>تاريخ الدورة: <b>{fmtDate(res.course_date)}</b></p>
              <p>رقم الشهادة: <b dir="ltr">{res.cert_no}</b></p>
              <p>تاريخ الإصدار: <b>{fmtDateTime(res.issued_at)}</b></p>
            </div>
          </div>
        ) : (
          <div className="card-lux !transform-none p-8 mt-8 border-red-300" data-testid="verify-result-invalid"><p className="flex items-center gap-2 text-red-700 font-bold"><ShieldX />لم يتم العثور على شهادة بهذا الرقم</p></div>
        ))}
      </div>
    </div>
  );
}
